import { useQueryClient } from '@tanstack/react-query'
import { CircleCheckIcon, CircleDashedIcon, DollarSignIcon, StarIcon, UsersIcon } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { BorderedCheckbox, Button, CheckboxGroup, EmailInput, InputBlock, Modal, TextInput } from 'slab'
import { getGetPartnersPartnerIdPartnerAdminsQueryKey, usePostPartnersPartnerIdPartnerAdmins } from 'utils/web_api'
import type { PartnersPartnerAdmin } from 'utils/web_api.schemas'

interface InvitePartnerAdminModalProps {
  partnerId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

const ROLE_OPTIONS = [
  { value: 'producer', label: 'Producer', icon: UsersIcon },
  { value: 'finance', label: 'Finance', icon: DollarSignIcon },
  { value: 'admin', label: 'Admin', icon: StarIcon },
]

const ROLE_PERMISSIONS = {
  producer: [
    'Owns prospects and clients',
    'Can request quotes',
  ],
  finance: [
    'Manages bank account information',
    'Sees all prospects and clients',
    'Sees financial reports',
  ],
  admin: [
    'Manages agency information',
    'Adds and removes team members',
  ],
}

interface FormData {
  first_name: string
  last_name: string
  email: string
  roles: string[]
}

const initialFormData: FormData = {
  first_name: '',
  last_name: '',
  email: '',
  roles: [],
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function InvitePartnerAdminModal ({ partnerId, isOpen, onClose, onSuccess }: InvitePartnerAdminModalProps) {
  const [apiError, setApiError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isValid }
  } = useForm<FormData>({
    defaultValues: initialFormData,
    mode: 'onTouched'
  })

  const watchedRoles = watch('roles')

  const createPartnerAdminMutation = usePostPartnersPartnerIdPartnerAdmins({
    mutation: {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [getGetPartnersPartnerIdPartnerAdminsQueryKey(partnerId)[0]]
        })
        reset()
        setApiError(null)

        const partnerAdminName = `${variables.data.first_name} ${variables.data.last_name}`.trim()
        toast.success(`${partnerAdminName} was invited`)

        onClose()
        onSuccess?.()
      },
      onError: (error: any) => {
        console.error('Failed to invite team member:', error)
        setApiError('Failed to invite team member. Please try again.')
      }
    }
  })

  const onSubmit = (data: FormData) => {
    setApiError(null)

    createPartnerAdminMutation.mutate({
      partnerId,
      data: {
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        email: data.email.trim(),
        roles: data.roles,
      } as PartnersPartnerAdmin
    })
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      setApiError(null)
      onClose()
    }
  }

  const getAllPermissions = () => {
    return ROLE_PERMISSIONS
  }

  const permissions = getAllPermissions()

  return (
    <Modal open={isOpen} onOpenChange={handleClose} width='large' aria-describedby={undefined}>
      <Modal.Header>
        <Modal.Title>Invite team member</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className='flex gap-0 -mr-6'>
          {/* Left Panel - Form */}
          <div className='w-1/2 pr-6 space-y-6'>
            {/* Name Fields - Separate lines */}
            <div className='space-y-4'>
              <InputBlock>
                <TextInput
                  label='First name'
                  placeholder='E.g. Jane'
                  {...register('first_name', {
                    required: 'First name is required',
                    onChange: () => setApiError(null)
                  })}
                  error={errors.first_name?.message}
                />
              </InputBlock>

              <InputBlock>
                <TextInput
                  label='Last name'
                  placeholder='E.g. Doe'
                  {...register('last_name', {
                    required: 'Last name is required',
                    onChange: () => setApiError(null)
                  })}
                  error={errors.last_name?.message}
                />
              </InputBlock>
            </div>

            {/* Email Field */}
            <InputBlock>
              <EmailInput
                label='Email address'
                placeholder='example@email.com'
                {...register('email', {
                  required: 'Email is required',
                  validate: (value) => isValidEmail(value) || 'Please enter a valid email address',
                  onChange: () => setApiError(null)
                })}
                error={errors.email?.message}
              />
            </InputBlock>

            {/* Role Selection */}
            <Controller
              name='roles'
              control={control}
              rules={{
                required: 'At least one role must be selected',
                validate: (value) => value.length > 0 || 'At least one role must be selected'
              }}
              render={({ field }) => (
                <CheckboxGroup
                  label='Select roles'
                  layout='horizontal'
                  gap={3}
                >
                  {ROLE_OPTIONS.map((option) => {
                    const Icon = option.icon
                    const isSelected = field.value.includes(option.value)

                    return (
                      <BorderedCheckbox
                        key={option.value}
                        checked={isSelected}
                        onChange={() => {
                          const newRoles = isSelected
                            ? field.value.filter((r: string) => r !== option.value)
                            : [...field.value, option.value]
                          field.onChange(newRoles)
                          setApiError(null)
                        }}
                        size='small'
                        icon={Icon}
                        label={option.label}
                      />
                    )
                  })}
                </CheckboxGroup>
              )}
            />
            {errors.roles && (
              <p className='text-sm text-red-600 mt-1'>{errors.roles.message}</p>
            )}
          </div>

          {/* Right Panel - Permissions Preview */}
          <div className='w-1/2 bg-gray-50 border-l border-gray-200 p-6 -m-6 ml-0 flex flex-col'>
            <p className='mb-6'>
              Invited members will have the following permissions:
            </p>

            <div className='space-y-6'>
              {Object.entries(permissions).map(([role, rolePermissions]) => {
                const roleOption = ROLE_OPTIONS.find(r => r.value === role)
                const Icon = roleOption?.icon || UsersIcon
                const isSelected = watchedRoles.includes(role)

                return (
                  <div key={role} className='space-y-2'>
                    <div className='flex items-center gap-2'>
                      <Icon size={16} className={isSelected ? '' : 'text-gray-400'} />
                      <span className={`body-bold ${isSelected ? '' : 'text-gray-400'}`}>
                        {roleOption?.label}
                      </span>
                    </div>

                    {role === 'admin' && (
                      <p className={`${isSelected ? '' : 'text-gray-400'}`}>
                        Includes all permissions from Producer and Finance, plus:
                      </p>
                    )}

                    <div className='space-y-1'>
                      {rolePermissions.map((permission, index) => (
                        <div key={index} className='flex items-center gap-2'>
                          {isSelected && (
                            <CircleCheckIcon size={16} fill='#29845A' className='text-white flex-shrink-0' />
                          )}
                          {!isSelected && (
                            <CircleDashedIcon size={16} className='text-gray-400 flex-shrink-0' />
                          )}
                          <span className={`${isSelected ? '' : 'text-gray-400'}`}>
                            {permission}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className='flex justify-between items-center'>
          {apiError && (
            <div className='text-red-600 text-sm'>
              {apiError}
            </div>
          )}
          <div className='flex justify-end gap-3 ml-auto'>
            <Button
              variant='tertiary'
              size='large'
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              size='large'
              onClick={() => handleSubmit(onSubmit)()}
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'Inviting...' : 'Invite'}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default InvitePartnerAdminModal
